import React, { useMemo, useState } from 'react'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { getAgency, getAgencyPrintout } from '@verzekeringapp-mono/shared/src/state/selectors/agency'
import { getThemeMode } from '@verzekeringapp-mono/shared/src/state/selectors/setup'
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect
} from 'react'
import {
  createThemeColors,
  ThemeColors,
  ThemeMode
} from '@verzekeringapp-mono/shared/src/utils/colors'

const getSystemThemeMode = (): ThemeMode => {
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark'
  }

  return 'light'
}

const defaultThemeColors = createThemeColors('light')

const themeColorVariables = (colors: ThemeColors) => ({
  '--color-brand-primary': colors.brand.primary,
  '--color-brand-on-primary': colors.brand.onPrimary,
  '--color-background-app': colors.background.app,
  '--color-background-chrome': colors.background.chrome,
  '--color-surface-page': colors.surface.page,
  '--color-surface-card': colors.surface.card,
  '--color-surface-hover': colors.surface.hover,
  '--color-surface-card-disabled': colors.surface.cardDisabled,
  '--color-surface-modal': colors.surface.modal,
  '--color-surface-input': colors.surface.input,
  '--color-surface-input-prefix': colors.surface.inputPrefix,
  '--color-surface-info': colors.surface.info,
  '--color-surface-chip': colors.surface.chip,
  '--color-surface-chip-selected': colors.surface.chipSelected,
  '--color-surface-inverse': colors.surface.inverse,
  '--color-surface-brand-subtle': colors.surface.brandSubtle,
  '--color-surface-track': colors.surface.track,
  '--color-surface-logo-backdrop': colors.surface.logoBackdrop,
  '--color-text-primary': colors.text.primary,
  '--color-text-secondary': colors.text.secondary,
  '--color-text-muted': colors.text.muted,
  '--color-text-placeholder': colors.text.placeholder,
  '--color-text-link': colors.text.link,
  '--color-text-brand': colors.text.brand,
  '--color-text-inverse': colors.text.inverse,
  '--color-text-on-brand': colors.text.onBrand,
  '--color-text-on-danger': colors.text.onDanger,
  '--color-text-on-neutral-strong': colors.text.onNeutralStrong,
  '--color-icon-primary': colors.icon.primary,
  '--color-icon-secondary': colors.icon.secondary,
  '--color-icon-muted': colors.icon.muted,
  '--color-icon-brand': colors.icon.brand,
  '--color-icon-inverse': colors.icon.inverse,
  '--color-icon-on-brand': colors.icon.onBrand,
  '--color-icon-on-danger': colors.icon.onDanger,
  '--color-icon-on-neutral-strong': colors.icon.onNeutralStrong,
  '--color-border-transparent': colors.border.transparent,
  '--color-border-subtle': colors.border.subtle,
  '--color-border-default': colors.border.default,
  '--color-border-strong': colors.border.strong,
  '--color-border-focus': colors.border.focus,
  '--color-border-brand': colors.border.brand,
  '--color-border-warning': colors.border.warning,
  '--color-border-danger': colors.border.danger,
  '--color-border-inverse': colors.border.inverse,
  '--color-action-primary-background': colors.action.primary.background,
  '--color-action-primary-foreground': colors.action.primary.foreground,
  '--color-action-primary-border': colors.action.primary.border,
  '--color-action-secondary-background': colors.action.secondary.background,
  '--color-action-secondary-foreground': colors.action.secondary.foreground,
  '--color-action-secondary-border': colors.action.secondary.border,
  '--color-action-outline-background': colors.action.outline.background,
  '--color-action-outline-foreground': colors.action.outline.foreground,
  '--color-action-outline-border': colors.action.outline.border,
  '--color-action-destructive-background': colors.action.destructive.background,
  '--color-action-destructive-foreground': colors.action.destructive.foreground,
  '--color-action-destructive-border': colors.action.destructive.border,
  '--color-action-destructive-outline-background': colors.action.destructiveOutline.background,
  '--color-action-destructive-outline-foreground': colors.action.destructiveOutline.foreground,
  '--color-action-destructive-outline-border': colors.action.destructiveOutline.border,
  '--color-action-whatsapp-filled-background': colors.action.whatsappFilled.background,
  '--color-action-whatsapp-filled-foreground': colors.action.whatsappFilled.foreground,
  '--color-action-whatsapp-filled-border': colors.action.whatsappFilled.border,
  '--color-action-whatsapp-outline-background': colors.action.whatsappOutline.background,
  '--color-action-whatsapp-outline-foreground': colors.action.whatsappOutline.foreground,
  '--color-action-whatsapp-outline-border': colors.action.whatsappOutline.border,
  '--color-status-success-foreground': colors.status.success.foreground,
  '--color-status-success-strong': colors.status.success.strong,
  '--color-status-success-subtle': colors.status.success.subtle,
  '--color-status-success-on-strong': colors.status.success.onStrong,
  '--color-status-warning-foreground': colors.status.warning.foreground,
  '--color-status-warning-strong': colors.status.warning.strong,
  '--color-status-warning-subtle': colors.status.warning.subtle,
  '--color-status-warning-on-strong': colors.status.warning.onStrong,
  '--color-status-danger-foreground': colors.status.danger.foreground,
  '--color-status-danger-strong': colors.status.danger.strong,
  '--color-status-danger-subtle': colors.status.danger.subtle,
  '--color-status-danger-on-strong': colors.status.danger.onStrong,
  '--color-sentiment-positive': colors.sentiment.positive,
  '--color-sentiment-neutral': colors.sentiment.neutral,
  '--color-sentiment-negative': colors.sentiment.negative,
  '--color-integration-whatsapp': colors.integrations.whatsapp,
  '--color-overlay-scrim': colors.overlay.scrim,
  '--color-overlay-scrim-soft': colors.overlay.scrimSoft,
  '--color-overlay-toast': colors.overlay.toast,
  '--color-shadow-default': colors.shadow.default
}) as const

const applyThemeVariables = (variables: Record<string, string>) => {
  Object.entries(variables).forEach(([cssVariable, value]) => {
    document.documentElement.style.setProperty(cssVariable, value)
  })
}

const ThemeColorProvider: FC<PropsWithChildren> = ({ children }) => {
  const agency = useAppSelector(getAgency)
  const printoutAgency = useAppSelector(getAgencyPrintout)
  const themeMode = useAppSelector(getThemeMode)
  const [systemThemeMode, setSystemThemeMode] = useState<ThemeMode>(
    getSystemThemeMode
  )

  const resolvedThemeMode: ThemeMode = themeMode ?? systemThemeMode
  const effectiveAgency = agency ?? printoutAgency

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => {
      setSystemThemeMode(event.matches ? 'dark' : 'light')
    }

    setSystemThemeMode(mediaQuery.matches ? 'dark' : 'light')

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange)

      return () => mediaQuery.removeEventListener('change', onChange)
    }

    mediaQuery.addListener(onChange)

    return () => mediaQuery.removeListener(onChange)
  }, [])

  const themeColors = useMemo<ThemeColors>(() => createThemeColors(resolvedThemeMode, {
    agencyBrand: effectiveAgency?.primaryColor || undefined,
    applyAgencyBrandInDark: false
  }), [effectiveAgency?.primaryColor, resolvedThemeMode])

  useEffect(() => {
    // Semantic CSS variables are the source of truth.
    applyThemeVariables(themeColorVariables(themeColors))

    document.documentElement.dataset.theme = resolvedThemeMode
    document.documentElement.style.colorScheme = resolvedThemeMode
    document.documentElement.classList.toggle(
      'dark',
      resolvedThemeMode === 'dark'
    )
  }, [resolvedThemeMode, themeColors])

  return (
    <ThemeColorContext.Provider
      value={{
        brandColors: {
          brand: themeColors.brand.primary,
          brandLight: themeColors.surface.brandSubtle
        },
        themeColors,
        themeMode: resolvedThemeMode
      }}
    >
      {children}
    </ThemeColorContext.Provider>
  )
}

const ThemeColorContext = createContext<{
  brandColors: {
    brand: string
    brandLight: string
  }
  themeColors: ThemeColors
  themeMode: ThemeMode
}>({
  brandColors: {
    brand: defaultThemeColors.brand.primary,
    brandLight: defaultThemeColors.surface.brandSubtle
  },
  themeColors: defaultThemeColors,
  themeMode: 'light'
})

export const useThemeColorContext = () => useContext(ThemeColorContext)

export default ThemeColorProvider
